/**
 * PushCampaignEditorPage.tsx
 *
 * Dedicated push campaign editor at /marketing/push-campaigns/:id.
 * Two-panel layout: editor (left) + notification preview (right).
 * Header bar with status/actions, contact list management,
 * send/schedule/review modals, baseline-comparison unsaved changes guard.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send, Bell, Trash2, Save, ArrowLeft,
  X, MoreVertical, Smile, Code,
  AlertTriangle, Users, XCircle,
  Info, Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';
import * as marketingSvc from '@/services/marketing.service';
import { apiClient } from '@/services/apiClient';
import { SmsEmojiPicker } from '@/components/sms-editor';
import { SMS_VARIABLES } from '@/components/sms-editor/sms-editor-constants';
import { DateTimePicker, nowAsDateTimeValue, toDateTimeValue } from '@/components/ui/DateTimePicker';

const PUSH_VARIABLES = SMS_VARIABLES.filter(v => v.key !== '{{opt_out}}');

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
};

const PUSH_TITLE_MAX = 65;
const PUSH_BODY_MAX = 240;

// In-app destinations the mobile app knows how to open (must mirror the deep-link
// resolver in next-health-app/src/navigation/navigationRef.ts). The admin picks
// one instead of typing a route, so every saved link is one the app can route.
const PUSH_DESTINATIONS: { value: string; label: string }[] = [
  { value: 'appointments', label: 'Appointments' },
  { value: 'profile', label: 'Profile / Account' },
  { value: 'health', label: 'Health' },
  { value: 'store', label: 'Store' },
  { value: 'home', label: 'Home' },
  { value: 'memberships', label: 'Memberships' },
  { value: 'my-memberships', label: 'My Memberships' },
  { value: 'payment-history', label: 'Payment History' },
  { value: 'book-now', label: 'Book Now' },
  { value: 'cart', label: 'Cart' },
];

// Parse a stored deepLink back into the picker's structured state.
function parseDeepLink(link: string): { kind: string; blogId: string; custom: string } {
  const l = (link || '').trim();
  if (!l) return { kind: 'none', blogId: '', custom: '' };
  if (/^https?:\/\//i.test(l)) return { kind: 'custom', blogId: '', custom: l };
  const path = l
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  const [head, idPart] = path.split('/');
  if (head === 'blog') return { kind: 'blog', blogId: idPart ?? '', custom: '' };
  if (PUSH_DESTINATIONS.some(d => d.value === head)) return { kind: head, blogId: '', custom: '' };
  // Unknown route — keep it as a custom value so it's preserved and editable.
  return { kind: 'custom', blogId: '', custom: l };
}

// Build the deepLink string from the picker's structured state.
function buildDeepLink(kind: string, blogId: string, custom: string): string {
  if (kind === 'none') return '';
  if (kind === 'custom') return custom.trim();
  if (kind === 'blog') return blogId ? `nexthealth://blog/${blogId}` : '';
  return `nexthealth://${kind}`;
}

function fmtDateTime(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function PushCampaignEditorPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const id = Number(idParam);

  // ── Campaign data ──
  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id],
    queryFn: () => marketingSvc.getPushCampaign(id),
    enabled: !!id,
  });

  // ── Local form state ──
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [deepLink, setDeepLink] = useState('');
  // Structured deep-link picker state (drives `deepLink`).
  const [linkKind, setLinkKind] = useState('none'); // 'none' | '<destination>' | 'blog' | 'custom'
  const [blogPostId, setBlogPostId] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [excludeAccountUsers, setExcludeAccountUsers] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [activeField, setActiveField] = useState<'title' | 'body'>('title');
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // ── Baseline for unsaved changes ──
  const baselineRef = useRef<Record<string, string>>({});

  // ── Initialize form from campaign ──
  useEffect(() => {
    if (campaign && !initialized) {
      const name = campaign.name ?? '';
      const desc = campaign.description ?? '';
      const title = campaign.title ?? '';
      const body = campaign.messageBody ?? '';
      const img = campaign.imageUrl ?? '';
      const link = campaign.deepLink ?? '';
      const sched = campaign.scheduledAt ? toDateTimeValue(new Date(campaign.scheduledAt)) : '';
      const exclUsers = !!campaign.accountUsers;

      setCampaignName(name);
      setCampaignDescription(desc);
      setPushTitle(title);
      setMessageBody(body);
      setImageUrl(img);
      setDeepLink(link);
      const parsed = parseDeepLink(link);
      setLinkKind(parsed.kind);
      setBlogPostId(parsed.blogId);
      setCustomUrl(parsed.custom);
      setScheduledAt(sched);
      setExcludeAccountUsers(exclUsers);

      baselineRef.current = {
        name, description: desc, title, messageBody: body,
        imageUrl: img, deepLink: link, scheduledAt: sched,
        excludeAccountUsers: String(exclUsers),
      };
      setInitialized(true);
    }
  }, [campaign, initialized]);

  // Keep `deepLink` in sync with the structured picker (round-trips cleanly with
  // parseDeepLink, so re-deriving on init doesn't spuriously mark the form dirty).
  useEffect(() => {
    setDeepLink(buildDeepLink(linkKind, blogPostId, customUrl));
  }, [linkKind, blogPostId, customUrl]);

  // Blog posts for the "Blog post" destination (fetched only when that's picked).
  const { data: blogPostsResp, isLoading: blogPostsLoading } = useQuery({
    queryKey: ['blog', 'posts', 'deeplink-picker'],
    queryFn: async () => (await apiClient.get('/blog/posts', { params: { limit: 200 } })).data,
    enabled: linkKind === 'blog',
    staleTime: 5 * 60 * 1000,
  });
  const blogPostOptions = useMemo(() => {
    const rows: any[] = blogPostsResp?.data ?? [];
    return rows.map((p) => ({
      value: String(p.blogPostID ?? p.id),
      label: String(p.title ?? `Post ${p.blogPostID ?? p.id}`),
    }));
  }, [blogPostsResp]);

  // ── Unsaved changes (baseline comparison) ──
  const hasUnsaved = useMemo(() => {
    if (!campaign || !initialized) return false;
    const b = baselineRef.current;
    if (campaignName !== b.name) return true;
    if (campaignDescription !== b.description) return true;
    if (pushTitle !== b.title) return true;
    if (messageBody !== b.messageBody) return true;
    if (imageUrl !== b.imageUrl) return true;
    if (deepLink !== b.deepLink) return true;
    if (scheduledAt !== b.scheduledAt) return true;
    if (String(excludeAccountUsers) !== b.excludeAccountUsers) return true;
    return false;
  }, [campaign, initialized, campaignName, campaignDescription, pushTitle, messageBody, imageUrl, deepLink, scheduledAt, excludeAccountUsers]);

  useUnsavedChangesGuard(hasUnsaved);

  // ── Lists ──
  const { data: attachedLists = [], refetch: refetchLists } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id, 'lists'],
    queryFn: () => marketingSvc.getPushCampaignLists(id),
    enabled: !!campaign,
  });

  const { data: availableLists = [] } = useQuery({
    queryKey: ['marketing', 'lists-all'],
    queryFn: () => marketingSvc.listAudienceLists({ perPage: 500 }).then(r => r.data),
    enabled: !!campaign,
  });

  // ── Recipient count ──
  const { data: recipientData, refetch: refetchRecipientCount } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id, 'recipient-count'],
    queryFn: () => marketingSvc.getPushCampaignRecipientCount(id),
    enabled: !!campaign,
  });
  const totalContacts = recipientData?.count ?? campaign?.totalRecipients ?? 0;
  const withTokenCount = recipientData?.withToken ?? 0;

  // ── Modals / UI state ──
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const [emojiPanelOpen, setEmojiPanelOpen] = useState(false);
  const [varPanelOpen, setVarPanelOpen] = useState(false);
  const [listTypeFilter, setListTypeFilter] = useState<'static' | 'dynamic'>('static');

  // ── Derived ──
  // Schedule floor, re-derived on a timer so a slot that was valid when picked starts
  // failing once it slips into the past (same treatment as the email editor).
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!scheduledAt) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [scheduledAt]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minScheduleAt = useMemo(() => nowAsDateTimeValue(), [nowTick]);
  const scheduleInPast = !!scheduledAt && scheduledAt < minScheduleAt;

  const isReady = campaignName.trim().length > 0 && pushTitle.trim().length > 0 && messageBody.trim().length > 0 && withTokenCount > 0 && !hasUnsaved && !scheduleInPast;

  // ── Close overflow menu on outside click ──
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);

  // ── Navigation ──
  const handleBack = useCallback(() => {
    navigate('/marketing/push-campaigns');
  }, [navigate]);

  // ── Mutations ──
  const saveMut = useMutation({
    mutationFn: () =>
      marketingSvc.updatePushCampaign(id, {
        name: campaignName,
        description: campaignDescription,
        title: pushTitle,
        messageBody,
        imageUrl: imageUrl || undefined,
        deepLink: deepLink || undefined,
        scheduledAt: scheduledAt || undefined,
        accountUsers: excludeAccountUsers,
      }),
    onSuccess: () => {
      baselineRef.current = {
        name: campaignName, description: campaignDescription,
        title: pushTitle, messageBody, imageUrl, deepLink, scheduledAt,
        excludeAccountUsers: String(excludeAccountUsers),
      };
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      toast.success('Campaign saved');
    },
    onError: () => toast.error('Failed to save campaign'),
  });

  const sendMut = useMutation({
    mutationFn: () => marketingSvc.sendPushCampaign(id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      const parts = [`${result.queued.toLocaleString()} queued for delivery`];
      if (result.noDevice > 0) parts.push(`${result.noDevice.toLocaleString()} skipped (no device)`);
      toast.success(parts.join(' · '));
      setTimeout(() => navigate('/marketing/push-campaigns'), 1000);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Send failed'),
  });

  const scheduleMut = useMutation({
    mutationFn: () => marketingSvc.schedulePushCampaign(id, scheduledAt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      toast.success('Campaign scheduled!');
    },
    onError: () => toast.error('Failed to schedule'),
  });

  const cancelScheduleMut = useMutation({
    mutationFn: () => marketingSvc.cancelPushSchedule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      toast.success('Schedule cancelled');
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => marketingSvc.deletePushCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      toast.success('Campaign deleted');
      navigate('/marketing/push-campaigns', { replace: true });
    },
    onError: () => toast.error('Failed to delete campaign'),
  });
  const handleDelete = useCallback(() => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;
    deleteMut.mutate();
  }, [deleteMut]);

  const addListMut = useMutation({
    mutationFn: (listId: number) => marketingSvc.addPushCampaignList(id, listId),
    onSuccess: () => { refetchLists(); refetchRecipientCount(); },
    onError: () => toast.error('Failed to add list'),
  });

  const removeListMut = useMutation({
    mutationFn: (listId: number) => marketingSvc.removePushCampaignList(id, listId),
    onSuccess: () => { refetchLists(); refetchRecipientCount(); },
    onError: () => toast.error('Failed to remove list'),
  });

  // ── Editor helpers ──
  const insertAtCursor = useCallback((text: string) => {
    if (activeField === 'title') {
      const el = titleRef.current;
      if (!el) { setPushTitle(prev => prev + text); return; }
      const s = el.selectionStart ?? pushTitle.length;
      const e = el.selectionEnd ?? pushTitle.length;
      setPushTitle(pushTitle.substring(0, s) + text + pushTitle.substring(e));
      requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + text.length; });
    } else {
      const ta = bodyRef.current;
      if (!ta) { setMessageBody(prev => prev + text); return; }
      const s = ta.selectionStart;
      const e = ta.selectionEnd;
      setMessageBody(messageBody.substring(0, s) + text + messageBody.substring(e));
      requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + text.length; });
    }
  }, [activeField, pushTitle, messageBody]);

  // ── Send / Schedule flow ──
  const confirmSendOrSchedule = async () => {
    setShowReviewModal(false);
    try {
      await marketingSvc.updatePushCampaign(id, {
        name: campaignName, description: campaignDescription,
        title: pushTitle, messageBody,
        imageUrl: imageUrl || undefined, deepLink: deepLink || undefined,
        scheduledAt: scheduledAt || undefined, accountUsers: excludeAccountUsers,
      });
      baselineRef.current = {
        name: campaignName, description: campaignDescription,
        title: pushTitle, messageBody, imageUrl, deepLink, scheduledAt,
        excludeAccountUsers: String(excludeAccountUsers),
      };
    } catch {
      toast.error('Save failed, aborting send');
      return;
    }
    if (scheduledAt) scheduleMut.mutate();
    else sendMut.mutate();
  };

  // ── Available lists for picker ──
  const attachedListIds = new Set(attachedLists.map(l => l.emailListId));
  const filteredAvailableLists = availableLists.filter(
    (l) => l.active && !attachedListIds.has(l.emailListId) && l.listType === listTypeFilter,
  );
  const listOptions = filteredAvailableLists.map((l) => ({
    value: l.emailListId,
    label: `${l.name} (${l.listType === 'dynamic' ? 'Dynamic' : 'Static'}) -- ${l.patientCount.toLocaleString()} members`,
  }));

  // ── Loading / Not found ──
  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16 text-slate-500">
        Campaign not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={handleBack}>Back to Campaigns</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <Bell className="h-4 w-4 text-violet-600" />
          <span className="font-semibold text-slate-800">Edit Campaign: {campaign.name}</span>
          <Badge variant={STATUS_BADGE[campaign.status] ?? 'neutral'} dot>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {hasUnsaved && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 border border-amber-300 bg-amber-50 rounded px-2 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          )}

          {campaign.status !== 'scheduled' && (
            <Button variant="ghost" size="sm" disabled={!isReady} onClick={() => setShowReviewModal(true)}>
              <Send className="h-4 w-4 mr-1" />
              {scheduledAt ? 'Schedule Now' : 'Send Now'}
            </Button>
          )}

          {campaign.status === 'scheduled' && (
            <Button variant="ghost" size="sm" className="text-amber-700 border-amber-300 bg-amber-50" onClick={() => cancelScheduleMut.mutate()}>
              <XCircle className="h-4 w-4 mr-1" /> Cancel Send
            </Button>
          )}

          <Button size="sm" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
            <Save className="h-4 w-4 mr-1" /> Save Changes
          </Button>

          {campaign.status === 'draft' && (
            <div className="relative" ref={overflowRef}>
              <button
                type="button"
                onClick={() => setShowOverflowMenu(p => !p)}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {showOverflowMenu && (
                <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => { setShowOverflowMenu(false); handleDelete(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Campaign
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scheduled banner */}
      {campaign.status === 'scheduled' && campaign.scheduledAt && (
        <div className="mx-4 mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>This campaign is scheduled to send on <strong>{fmtDateTime(campaign.scheduledAt)}</strong></span>
        </div>
      )}

      {/* Main layout: editor + phone preview */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-[3] overflow-y-auto">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-violet-600" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Push Notification Details</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">Total Contacts</span>
                  <span className="text-2xl font-bold text-violet-500">{totalContacts.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-600">With Push Token</span>
                  <span className="text-2xl font-bold text-emerald-500">{withTokenCount.toLocaleString()}</span>
                </div>
                <span className="text-[11px] text-slate-400">Only contacts with a push token will receive notifications</span>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
          {/* Campaign Name */}
          <FormField label="Campaign Name" required>
            <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Enter campaign name" />
          </FormField>

          {/* Notification Title */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-slate-700">Notification Title <span className="text-red-500">*</span></label>
              <span className={cn('text-xs', pushTitle.length > PUSH_TITLE_MAX ? 'text-red-500 font-semibold' : 'text-slate-400')}>
                {pushTitle.length}/{PUSH_TITLE_MAX}
              </span>
            </div>
            <Input
              ref={titleRef}
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              onFocus={() => setActiveField('title')}
              placeholder="e.g., Special Offer Just for You!"
              maxLength={PUSH_TITLE_MAX}
            />
          </div>

          {/* Notification Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-slate-700">Notification Body <span className="text-red-500">*</span></label>
              <span className={cn('text-xs', messageBody.length > PUSH_BODY_MAX ? 'text-red-500 font-semibold' : 'text-slate-400')}>
                {messageBody.length}/{PUSH_BODY_MAX}
              </span>
            </div>
            <PushEditor
              messageBody={messageBody}
              onMessageChange={setMessageBody}
              bodyRef={bodyRef}
              onFocusBody={() => setActiveField('body')}
              insertAtCursor={insertAtCursor}
              emojiPanelOpen={emojiPanelOpen}
              setEmojiPanelOpen={setEmojiPanelOpen}
              varPanelOpen={varPanelOpen}
              setVarPanelOpen={setVarPanelOpen}
            />
          </div>

          {/* Image URL */}
          <FormField label="Image URL (optional)" helpText="Rich push notifications can include an image. Enter the full URL.">
            <div className="flex items-center gap-2">
              <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" className="flex-1" />
              {imageUrl && (
                <button onClick={() => setImageUrl('')} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
              )}
            </div>
            {imageUrl && (
              <div className="mt-2 w-32 h-20 rounded border border-slate-200 overflow-hidden">
                <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </FormField>

          {/* Deep Link — pick a destination instead of typing a route */}
          <FormField label="When tapped, open (optional)" helpText="Where the notification takes the user in the app">
            <Select
              value={linkKind}
              onChange={(v) => setLinkKind(v ?? 'none')}
              options={[
                { value: 'none', label: '— Nothing (just show the message) —' },
                ...PUSH_DESTINATIONS,
                { value: 'blog', label: 'Blog post…' },
                { value: 'custom', label: 'Custom web URL…' },
              ]}
            />
            {linkKind === 'blog' && (
              <div className="mt-2">
                <Select
                  value={blogPostId}
                  onChange={(v) => setBlogPostId(v ?? '')}
                  options={blogPostOptions}
                  placeholder={blogPostsLoading ? 'Loading posts…' : 'Select a blog post…'}
                  searchable
                />
              </div>
            )}
            {linkKind === 'custom' && (
              <div className="mt-2">
                <Input
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}
            {deepLink ? (
              <p className="mt-2 text-xs text-slate-500">
                Deep link: <span className="font-mono">{deepLink}</span>
              </p>
            ) : null}
          </FormField>

          {/* Description */}
          <FormField label="Description" helpText="For internal reference only -- not visible to recipients">
            <Textarea autoResize value={campaignDescription} onChange={(e) => setCampaignDescription(e.target.value)} rows={2} placeholder="Internal notes about this campaign..." />
          </FormField>

          {/* Schedule */}
          <FormField label="Schedule Send" helpText="Leave blank to send manually. Set a date and time to enable scheduling." htmlFor="push-schedule-at">
            <DateTimePicker
              id="push-schedule-at"
              value={scheduledAt}
              min={minScheduleAt}
              onChange={setScheduledAt}
            />
          </FormField>

          {/* Campaign Contacts Section */}
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-2">Campaign Contacts</label>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-violet-50 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-violet-500">{totalContacts.toLocaleString()}</span>
                    <span className="text-sm font-semibold text-slate-700">total contacts</span>
                  </div>
                  <div className="h-6 w-px bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-emerald-500">{withTokenCount.toLocaleString()}</span>
                    <span className="text-sm font-semibold text-slate-700">with push token</span>
                  </div>
                </div>
                <span className="text-xs text-slate-500 italic">Reachable via push notification</span>
              </div>

              <div className="px-4 py-3 border-b border-slate-200">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl font-bold text-indigo-500">{attachedLists.length}</span>
                  <span className="text-sm font-semibold text-slate-700">contact {attachedLists.length === 1 ? 'list' : 'lists'}</span>
                </div>
                <span className="text-xs text-slate-500 italic block mb-3">Attach reusable lists to include their members when sending</span>

                <div className="flex items-center gap-2 mb-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setListTypeFilter('static')}
                      className={cn('px-2 py-1 text-xs font-semibold rounded border transition-colors',
                        listTypeFilter === 'static' ? 'bg-blue-50 border-blue-600 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100')}
                    >Static</button>
                    <button
                      onClick={() => setListTypeFilter('dynamic')}
                      className={cn('px-2 py-1 text-xs font-semibold rounded border transition-colors',
                        listTypeFilter === 'dynamic' ? 'bg-orange-50 border-orange-500 text-orange-700' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100')}
                    >Dynamic</button>
                  </div>
                  <div className="flex-1">
                    <Select placeholder={`Search and select a ${listTypeFilter} list...`} options={listOptions} value={null} onChange={(val) => { if (val) addListMut.mutate(val); }} searchable clearable />
                  </div>
                </div>

                {attachedLists.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-1">No lists attached to this campaign</p>
                ) : (
                  <div className="space-y-1.5">
                    {attachedLists.map((li) => (
                      <div key={li.emailListId} className="flex items-center justify-between px-3 py-2 border border-slate-100 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold text-slate-700 truncate">{li.listName}</span>
                          <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', li.listType === 'dynamic' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700')}>
                            {li.listType === 'dynamic' ? 'Dynamic' : 'Static'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-slate-500">{li.memberCount.toLocaleString()} members</span>
                          <span className="text-xs font-semibold text-emerald-600">{li.pushTokenCount.toLocaleString()} with token</span>
                          <button onClick={() => removeListMut.mutate(li.emailListId)} className="text-slate-300 hover:text-red-500 hover:bg-red-50 rounded p-0.5 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-4 py-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={excludeAccountUsers} onChange={(e) => setExcludeAccountUsers(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-500 focus:ring-violet-500" />
                  <span className="text-sm font-semibold text-slate-600">Exclude account users from this campaign</span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-7">When checked, account users will be automatically excluded when sending</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Only contacts with a registered push notification token will receive the notification.</p>
          </div>
          </div>
        </div>

        {/* Phone Preview Panel */}
        <div className="flex-[2] bg-slate-100 flex flex-col items-center justify-start pt-6 overflow-y-auto">
          <div className="text-center mb-3 text-sm font-semibold text-slate-500">Preview</div>
          <PushNotificationMockup title={pushTitle} body={messageBody} imageUrl={imageUrl} />
        </div>
      </div>

      {/* Review & Send/Schedule Modal */}
      <Modal isOpen={showReviewModal} onClose={() => setShowReviewModal(false)} title={scheduledAt ? 'Review & Schedule Campaign' : 'Review & Send Campaign'} size="lg">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-violet-500" />
                  <span className="text-2xl font-bold text-violet-500">{totalContacts.toLocaleString()}</span>
                  <span className="text-sm font-semibold text-slate-500">contacts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-emerald-500" />
                  <span className="text-2xl font-bold text-emerald-500">{withTokenCount.toLocaleString()}</span>
                  <span className="text-sm font-semibold text-slate-500">reachable</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                <strong>This action cannot be undone.</strong>{' '}
                {scheduledAt
                  ? 'Once scheduled, this campaign will be delivered to all reachable recipients at the scheduled time.'
                  : 'Once sent, this campaign will be delivered to all reachable recipients immediately.'}
              </span>
            </div>

            {withTokenCount < totalContacts && (
              <div className="flex items-center gap-2.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
                <Info className="h-4 w-4 flex-shrink-0" />
                <span><strong>{(totalContacts - withTokenCount).toLocaleString()}</strong> contacts do not have a push token and will not receive this notification.</span>
              </div>
            )}

            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-slate-100"><td className="py-2 font-semibold text-slate-500 w-32">Campaign</td><td className="py-2 text-slate-800">{campaignName}</td></tr>
                <tr className="border-b border-slate-100"><td className="py-2 font-semibold text-slate-500">Type</td><td className="py-2 text-slate-800">Push Notification</td></tr>
                {scheduledAt && <tr className="border-b border-slate-100"><td className="py-2 font-semibold text-slate-500">Scheduled For</td><td className="py-2 text-slate-800">{fmtDateTime(scheduledAt)}</td></tr>}
              </tbody>
            </table>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setShowReviewModal(false)}>Cancel</Button>
              <Button
                className={scheduledAt ? 'bg-orange-500 hover:bg-orange-600' : 'bg-violet-600 hover:bg-violet-700'}
                loading={sendMut.isPending || scheduleMut.isPending}
                onClick={confirmSendOrSchedule}
              >
                <Send className="h-4 w-4 mr-1" />
                {scheduledAt ? 'Schedule Now' : 'Send Now'}
              </Button>
            </div>
          </div>

          <div className="w-64 flex-shrink-0 flex flex-col items-center">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Notification Preview</div>
            <PushNotificationMockup title={pushTitle} body={messageBody} imageUrl={imageUrl} compact />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Push Editor Sub-Component
// ═══════════════════════════════════════════════════════════════════════════

interface PushEditorProps {
  messageBody: string;
  onMessageChange: (val: string) => void;
  bodyRef: React.RefObject<HTMLTextAreaElement>;
  onFocusBody: () => void;
  insertAtCursor: (text: string) => void;
  emojiPanelOpen: boolean;
  setEmojiPanelOpen: (v: boolean) => void;
  varPanelOpen: boolean;
  setVarPanelOpen: (v: boolean) => void;
}

function PushEditor({
  messageBody, onMessageChange, bodyRef, onFocusBody,
  insertAtCursor,
  emojiPanelOpen, setEmojiPanelOpen, varPanelOpen, setVarPanelOpen,
}: PushEditorProps) {
  const varPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!varPanelOpen) return;
    const handler = (e: MouseEvent) => {
      if (varPanelRef.current && !varPanelRef.current.contains(e.target as Node)) {
        setVarPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [varPanelOpen, setVarPanelOpen]);

  return (
    <div className="border border-slate-200 rounded-lg focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100">
      <textarea
        ref={bodyRef}
        className="w-full min-h-[120px] px-3 py-3 text-sm leading-relaxed resize-y border-none outline-none font-sans rounded-t-lg"
        placeholder="Type your notification message..."
        value={messageBody}
        onChange={(e) => onMessageChange(e.target.value)}
        onFocus={onFocusBody}
        maxLength={PUSH_BODY_MAX}
      />
      <div className="relative flex items-center gap-1 px-2.5 py-2 bg-slate-50 border-t border-slate-100 rounded-b-lg">
        <div className="relative">
          <button onClick={() => { setEmojiPanelOpen(!emojiPanelOpen); setVarPanelOpen(false); }} className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors" title="Insert emoji">
            <Smile className="h-4 w-4" />
          </button>
          {emojiPanelOpen && (
            <SmsEmojiPicker onSelect={(emoji) => { insertAtCursor(emoji); setEmojiPanelOpen(false); }} onClose={() => setEmojiPanelOpen(false)} />
          )}
        </div>
        <div className="relative" ref={varPanelRef}>
          <button onClick={() => { setVarPanelOpen(!varPanelOpen); setEmojiPanelOpen(false); }} className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors" title="Insert variable">
            <Code className="h-4 w-4" />
          </button>
          {varPanelOpen && (
            <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
              {PUSH_VARIABLES.map((v) => (
                <button key={v.key} onClick={() => { insertAtCursor(v.key); setVarPanelOpen(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-b-0">
                  <code className="font-semibold text-violet-500">{v.key}</code>
                  <span className="block text-[11px] text-slate-400">{v.desc}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="ml-auto text-[11px] text-slate-400">Inserts into {'\u201C'}active field{'\u201D'} (title or body)</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Push Notification Mockup
// ═══════════════════════════════════════════════════════════════════════════

function PushNotificationMockup({ title, body, imageUrl, compact = false }: { title: string; body: string; imageUrl?: string; compact?: boolean }) {
  const width = compact ? 'w-[260px]' : 'w-[320px]';
  return (
    <div className={cn(width)}>
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
          <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Bell className="h-3 w-3 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Next Health</span>
          <span className="ml-auto text-[10px] text-slate-400">now</span>
        </div>
        <div className="p-3">
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <p className={cn('font-semibold text-slate-900 leading-snug', compact ? 'text-xs' : 'text-sm')}>{title || 'Notification Title'}</p>
              <p className={cn('text-slate-600 mt-0.5 leading-snug', compact ? 'text-[11px] line-clamp-3' : 'text-xs line-clamp-4')}>{body || 'Your notification message will appear here...'}</p>
            </div>
            {imageUrl && (
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-100">
                <img src={imageUrl} alt="Notification" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>
        {imageUrl && !compact && (
          <div className="px-3 pb-3">
            <div className="rounded-lg overflow-hidden border border-slate-100">
              <img src={imageUrl} alt="Rich notification" className="w-full h-auto max-h-40 object-cover" />
            </div>
          </div>
        )}
      </div>
      <div className="mt-3 text-center">
        <div className="inline-flex items-center gap-1 text-[10px] text-slate-400">
          <Smartphone className="h-3 w-3" />
          <span>Push Notification Preview</span>
        </div>
      </div>
    </div>
  );
}
